import type { DepositBanditMethodSuggestions, DepositBanditPaymentMethod, PaymentVendorMetadata } from 'models/reqres';
import { FundMethods } from 'stores/zustand/registrationStore';

export const DEPOSIT_BANDIT_PAYMENT_METHODS: DepositBanditPaymentMethod[] = ['bank', 'card', 'paypal', 'venmo'];

// Maps vendor name to deposit bandit payment method (mirrors backend vendorToSuggestionMethod)
const VENDOR_TO_SUGGESTION_METHOD: Record<string, DepositBanditPaymentMethod> = {
    stripe: 'card',
    checkout: 'card',
    plaid: 'bank',
    aeropay: 'bank',
    paypal: 'paypal',
    venmo: 'venmo',
};

/** Extracts deposit suggestions from per-vendor metadata into a method-keyed map. */
export const extractDepositSuggestionsByMethod = (
    vendors: PaymentVendorMetadata[],
): Partial<Record<DepositBanditPaymentMethod, DepositBanditMethodSuggestions>> => {
    const result: Partial<Record<DepositBanditPaymentMethod, DepositBanditMethodSuggestions>> = {};
    for (const v of vendors) {
        if (!v.deposit_suggestions) continue;
        const method = VENDOR_TO_SUGGESTION_METHOD[v.vendor];
        if (method && !result[method]) {
            result[method] = v.deposit_suggestions;
        }
    }
    return result;
};

export const toDepositBanditPaymentMethod = (
    fundMethod: FundMethods | undefined,
): DepositBanditPaymentMethod | null => {
    if (fundMethod === FundMethods.BANK) return 'bank';
    if (fundMethod === FundMethods.DEBIT_CARD) return 'card';
    if (fundMethod === FundMethods.BRAINTREE_PAYPAL) return 'paypal';
    if (fundMethod === FundMethods.BRAINTREE_VENMO) return 'venmo';
    return null;
};

interface ResolveDepositBanditPresentationParams {
    paymentMethod: DepositBanditPaymentMethod;
    activeSuggestionKey: string | null;
    activeSuggestion: DepositBanditMethodSuggestions | undefined;
    defaultPresetAmounts: number[];
}

interface DepositBanditPresentation {
    banditSuggestion: DepositBanditMethodSuggestions | undefined;
    presetAmounts: number[];
    armId: number;
    selectionMode: 'baseline' | 'explore' | 'exploit' | 'segment_default' | 'fallback';
    epsilon: number;
}

export const resolveDepositBanditPresentation = ({
    paymentMethod,
    activeSuggestionKey,
    activeSuggestion,
    defaultPresetAmounts,
}: ResolveDepositBanditPresentationParams): DepositBanditPresentation => {
    const expectedSuggestionKey = `web:${paymentMethod}`;
    const banditSuggestion = activeSuggestionKey === expectedSuggestionKey ? activeSuggestion : undefined;
    const presetAmounts =
        banditSuggestion && banditSuggestion.suggestions.length > 0
            ? banditSuggestion.suggestions
            : defaultPresetAmounts;

    return {
        banditSuggestion,
        presetAmounts,
        armId: banditSuggestion ? banditSuggestion.arm_id : -1,
        selectionMode: banditSuggestion?.selection_mode ?? 'baseline',
        epsilon: banditSuggestion?.epsilon ?? 0,
    };
};
